import React, { useState, useEffect, useContext, useRef } from 'react';
import { Form, Input, Table } from 'antd';
import { format } from 'date-fns';

const { TextArea } = Input;

// Helper function to format dates
const formatDate = (date) => {
  if (!date) return '-';
  try {
    return format(new Date(date), 'dd/MM/yyyy');
  } catch (e) {
    return 'Invalid';
  }
};

// Helper function to format date display in cells
const formatDateDisplay = (dateStr) => {
  if (!dateStr) return '-';
  
  // First check if it's already in YYYY-MM-DD format and convert to DD/MM/YYYY
  const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    const [_, year, month, day] = isoMatch;
    return `${day}/${month}/${year}`;
  }
  
  // If it's already in DD/MM/YYYY format, leave it as is
  const dmyMatch = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (dmyMatch) {
    return dateStr;
  }
  
  // If all else fails, try to format using date-fns
  try {
    return format(new Date(dateStr), 'dd/MM/yyyy');
  } catch (e) {
    return dateStr; // Return the original string if we can't format it
  }
};

// Create EditableContext
const EditableContext = React.createContext(null);

// Create KeyboardNavContext
const KeyboardNavContext = React.createContext({
  activeCell: null,
  setActiveCell: () => {},
  navigateToCell: () => {},
  activeRowKey: null,
  setActiveRowKey: () => {},
  pendingChanges: new Map(),
  setPendingChanges: () => {},
  getCellValue: (rowIndex, colIndex, dataIndex) => null
});

// EditableRow component
const EditableRow = ({ index, isModified, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

// EditableCell component
const EditableCell = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleChange,
  isModified,
  inputType,
  rowIndex,
  colIndex,
  navigateToCell,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  const form = useContext(EditableContext);
  const { 
    setActiveRowKey, 
    pendingChanges, 
    setPendingChanges,
    getCellValue 
  } = useContext(KeyboardNavContext);
  
  // For debugging - create a reference to store the cell position info
  const cellInfoRef = useRef({
    rowIndex,
    colIndex,
    dataIndex,
    recordId: record?.id || null
  });

  // Update the ref when props change
  useEffect(() => {
    if (record) {
      cellInfoRef.current = {
        rowIndex,
        colIndex,
        dataIndex,
        recordId: record.id || null
      };
    }
  }, [rowIndex, colIndex, dataIndex, record]);

  useEffect(() => {
    if (editing && record) {
      inputRef.current?.focus();
      
      // First check if there's a pending change for this field
      const hasPendingChange = pendingChanges.has(record.id) && 
                              pendingChanges.get(record.id)[dataIndex] !== undefined;
      let valueToShow;
      
      if (hasPendingChange) {
        // Use the pending change value
        valueToShow = pendingChanges.get(record.id)[dataIndex];
        
        // If it's a date field in YYYY-MM-DD format, convert to DD/MM/YYYY for display
        if (dataIndex === 'date_exp') {
          const dateMatch = valueToShow.match(/^(\d{4})-(\d{2})-(\d{2})$/);
          if (dateMatch) {
            const [_, year, month, day] = dateMatch;
            valueToShow = `${day}/${month}/${year}`;
          }
        }
      } else if (dataIndex === 'date_exp' && record[dataIndex]) {
        // Format date for display when entering edit mode (YYYY-MM-DD to DD/MM/YYYY)
        try {
          const dateMatch = record[dataIndex].match(/^(\d{4})-(\d{2})-(\d{2})$/);
          if (dateMatch) {
            const [_, year, month, day] = dateMatch;
            valueToShow = `${day}/${month}/${year}`;
          } else {
            valueToShow = record[dataIndex];
          }
        } catch (e) {
          valueToShow = record[dataIndex];
        }
      } else {
        // Use the original record value
        valueToShow = record[dataIndex];
      }
      
      // Set the field value
      form.setFieldsValue({ [dataIndex]: valueToShow });
      
      // Select the text for easy editing - but not for TextArea
      if (inputRef.current && dataIndex !== 'obser' && typeof inputRef.current.select === 'function') {
        // Need a small delay to ensure field is populated before selecting
        setTimeout(() => {
          inputRef.current?.select();
        }, 50);
      }
      
      // Set the active row when editing begins
      if (record.id) {
        setActiveRowKey(record.id);
      }
    }
  }, [editing, record, setActiveRowKey, dataIndex, form, pendingChanges]);

  const toggleEdit = () => {
    if (!record) return;
    setEditing(!editing);
  };

  const saveToLocalChanges = async () => {
    if (!record) return;
    
    try {
      const values = await form.validateFields();
      let processedValue = values[dataIndex];
      
      // Special handling for dates - convert from DD/MM/YYYY to YYYY-MM-DD
      if (dataIndex === 'date_exp' && processedValue) {
        // Check if it matches the format DD/MM/YYYY
        const dateMatch = processedValue.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (dateMatch) {
          const [_, day, month, year] = dateMatch;
          // Convert to YYYY-MM-DD format for backend
          processedValue = `${year}-${month}-${day}`;
        }
      }
      
      // Only add to pendingChanges if value actually changed
      const currentValue = record[dataIndex];
      const newValue = processedValue;
      
      // Check if values are different
      if (String(currentValue) !== String(newValue)) {
        // Get or create changes object for this record
        const recordChanges = pendingChanges.get(record.id) || {};
        
        // Add the changed field
        recordChanges[dataIndex] = newValue;
        
        // Update the pending changes map with a new Map to ensure state update
        const newPendingChanges = new Map(pendingChanges);
        newPendingChanges.set(record.id, recordChanges);
        setPendingChanges(newPendingChanges);
      }
      
      setEditing(false);
    } catch (errInfo) {
      console.log('Validation failed:', errInfo);
    }
  };

  // Cancel editing
  const cancel = () => {
    if (!record) return;
    setEditing(false);
  };

  // Handle keyboard navigation
 // Handle keyboard navigation
const handleKeyDown = (e) => {
    // Check for Ctrl+3 to copy value from cell above
    if (e.ctrlKey && e.key === '3') {
      e.preventDefault();
      
      // We need to get the value from the cell above
      if (rowIndex > 0 && record) {
        // Since getCellValue is not available, we'll implement a direct approach
        // to get the value from the cell above
        
        try {
          // Find all data rows in the table
          const rows = document.querySelectorAll('.ant-table-row');
          if (rows.length > rowIndex - 1) {
            // Get the row above
            const rowAbove = rows[rowIndex - 1];
            
            // Find all editable cells in that row
            const cellsInRowAbove = rowAbove.querySelectorAll('.editable-cell-value-wrap');
            
            // Find the cell with the matching column index
            const targetCellsInRowAbove = Array.from(cellsInRowAbove).filter(
              cell => parseInt(cell.getAttribute('data-col-index')) === colIndex
            );
            
            if (targetCellsInRowAbove.length > 0) {
              const cellAbove = targetCellsInRowAbove[0];
              const valueFromAbove = cellAbove.textContent.trim();
              
              // Set the value in the form
              form.setFieldsValue({ [dataIndex]: valueFromAbove });
              
              // Add to pending changes
              const recordChanges = pendingChanges.get(record.id) || {};
              recordChanges[dataIndex] = valueFromAbove;
              
              // Update the pending changes map
              const newPendingChanges = new Map(pendingChanges);
              newPendingChanges.set(record.id, recordChanges);
              setPendingChanges(newPendingChanges);
            }
          }
        } catch (error) {
          console.error('Error copying value from cell above:', error);
        }
      }
      return;
    }
    
    // Existing key handlers remain unchanged
    switch (e.key) {
      case 'Enter':
        e.preventDefault();
        saveToLocalChanges();
        setTimeout(() => navigateToCell(rowIndex + 1, colIndex), 100);
        break;
      case 'Escape':
        e.preventDefault();
        cancel();
        break;
      case 'Tab':
        e.preventDefault();
        saveToLocalChanges();
        setTimeout(() => navigateToCell(rowIndex, colIndex + 1), 100);
        break;
      case 'ArrowUp':
        e.preventDefault();
        saveToLocalChanges();
        setTimeout(() => navigateToCell(rowIndex - 1, colIndex), 100);
        break;
      case 'ArrowDown':
        e.preventDefault();
        saveToLocalChanges();
        setTimeout(() => navigateToCell(rowIndex + 1, colIndex), 100);
        break;
      case 'ArrowLeft':
        // Only navigate if at beginning of input
        if (e.target.selectionStart === 0 && e.target.selectionEnd === 0) {
          e.preventDefault();
          saveToLocalChanges();
          setTimeout(() => navigateToCell(rowIndex, colIndex - 1), 100);
        }
        break;
      case 'ArrowRight':
        // Only navigate if at end of input
        if (e.target.selectionStart === e.target.value.length) {
          e.preventDefault();
          saveToLocalChanges();
          setTimeout(() => navigateToCell(rowIndex, colIndex + 1), 100);
        }
        break;
      default:
        break;
    }
  }

  // Handle focus lost
  const handleBlur = (e) => {
    // Handle date auto-completion on blur
    if (dataIndex === 'date_exp') {
      const value = e.target.value;
      
      // If user entered just day and month (DD/MM), add current year
      if (/^\d{2}\/\d{2}$/.test(value)) {
        const currentYear = new Date().getFullYear();
        const newValue = value + '/' + currentYear;
        form.setFieldsValue({ [dataIndex]: newValue });
      }
    }
    
    // Continue with save operation
    if (editing) {
      saveToLocalChanges();
    }
  };

  // Render different input types based on the dataIndex
  const getInputNode = () => {
    if (dataIndex === 'obser') {
      return <TextArea
               ref={inputRef}
               rows={1}
               size="small"
               onKeyDown={handleKeyDown}
               onBlur={handleBlur}
             />;
    } else {
      // All other fields use regular text input
      return <Input
               ref={inputRef}
               size="small"
               onKeyDown={handleKeyDown}
               onBlur={handleBlur}
               placeholder={dataIndex === 'date_exp' ? "JJ/MM/AAAA" : ""}
               onChange={(e) => {
                 // Apply auto-formatting for date fields during typing
                 if (dataIndex === 'date_exp') {
                   let value = e.target.value;
                   let shouldUpdateCursor = false;
                   
                   // Only allow digits and slashes
                   value = value.replace(/[^\d/]/g, '');
                   
                   // Auto-format as user types
                   if (value.length === 2 && !value.includes('/')) {
                     value += '/';
                     shouldUpdateCursor = true;
                   } else if (value.length === 5 && value.indexOf('/', 3) === -1) {
                     value += '/';
                     shouldUpdateCursor = true;
                   }
                   
                   // Update the input value
                   if (value !== e.target.value) {
                     e.target.value = value;
                     
                     // Force update the form value too to ensure state consistency
                     form.setFieldsValue({ [dataIndex]: value });
                     
                     // Set cursor position to the end (AFTER the automatically added slash)
                     if (shouldUpdateCursor) {
                       // Use requestAnimationFrame to ensure this happens after React rendering
                       requestAnimationFrame(() => {
                         e.target.selectionStart = value.length;
                         e.target.selectionEnd = value.length;
                         
                         // Double-check with another setTimeout to ensure it sticks
                         setTimeout(() => {
                           e.target.selectionStart = value.length;
                           e.target.selectionEnd = value.length;
                         }, 10);
                       });
                     }
                   }
                 }
               }}
             />;
    }
  };

  let childNode = children;

  if (editable && record) {
    childNode = editing ? (
      <div className="compact-edit-container">
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
        >
          {getInputNode()}
        </Form.Item>
      </div>
    ) : (
      <div 
        className="editable-cell-value-wrap" 
        style={{ 
          paddingRight: 12,
          color: pendingChanges.has(record.id) && pendingChanges.get(record.id)[dataIndex] !== undefined ? '#1890ff' : 'inherit',
          fontWeight: pendingChanges.has(record.id) && pendingChanges.get(record.id)[dataIndex] !== undefined ? 'bold' : 'normal',
          cursor: 'pointer'
        }} 
        onClick={toggleEdit}
        data-row-index={rowIndex}
        data-col-index={colIndex}
        data-cell-id={`${record.id}-${dataIndex}`}
      >
        {/* Display the pending change value instead of the original value if it exists */}
        {pendingChanges.has(record.id) && pendingChanges.get(record.id)[dataIndex] !== undefined 
          ? (dataIndex === 'date_exp' 
              ? formatDateDisplay(pendingChanges.get(record.id)[dataIndex]) 
              : dataIndex === 'mt_remb' && pendingChanges.get(record.id)[dataIndex] 
                ? parseFloat(pendingChanges.get(record.id)[dataIndex]).toFixed(2)
                : pendingChanges.get(record.id)[dataIndex])
          : dataIndex === 'date_exp' && children ? formatDateDisplay(record[dataIndex]) : children}
      </div>
    );
  }

  return <td {...restProps}>{childNode}</td>;
};

// Main EditableTable component
const EditableTable = ({ dataSource, columns, rowKey = 'id', onSave }) => {
  const [data, setData] = useState([]);
  const [pendingChanges, setPendingChanges] = useState(new Map());
  const [activeRowKey, setActiveRowKey] = useState(null);
  const [activeCell, setActiveCell] = useState(null);
  
  useEffect(() => {
    setData(dataSource);
  }, [dataSource]);
  
  // Get cell DOM element by position
  const getCellDOMElement = (rowIndex, colIndex) => {
    const cells = document.querySelectorAll(`[data-row-index="${rowIndex}"][data-col-index="${colIndex}"]`);
    return cells.length > 0 ? cells[0] : null;
  };
  
  // Navigate to a specific cell
  const navigateToCell = (rowIndex, colIndex) => {
    // Make sure indices are within bounds
    if (rowIndex < 0 || colIndex < 0) return;
    if (rowIndex >= data.length) rowIndex = data.length - 1;
    
    // Find all editable columns
    const editableColumns = columns.filter(col => col.editable);
    if (colIndex >= editableColumns.length) colIndex = editableColumns.length - 1;
    
    // Get the column dataIndex
    const dataIndex = editableColumns[colIndex]?.dataIndex;
    if (!dataIndex) return;
    
    // Get the record
    const record = data[rowIndex];
    if (!record) return;
    
    // Set active cell
    setActiveCell({
      rowIndex,
      colIndex,
      recordId: record.id,
      dataIndex
    });
    
    // Find and click the cell element
    const cellElement = getCellDOMElement(rowIndex, colIndex);
    if (cellElement) {
      cellElement.click();
    }
  };
  
  // Get cell value by position
  const getCellValue = (rowIndex, colIndex, targetDataIndex) => {
    if (rowIndex < 0 || rowIndex >= data.length) {
      return null;
    }
    
    // Get the record
    const record = data[rowIndex];
    if (!record) return null;
    
    // If no dataIndex is provided, find it from the columns
    let dataIndex = targetDataIndex;
    if (!dataIndex) {
      // Find all editable columns
      const editableColumns = columns.filter(col => col.editable);
      if (colIndex >= editableColumns.length) return null;
      
      // Get the column dataIndex
      dataIndex = editableColumns[colIndex]?.dataIndex;
      if (!dataIndex) return null;
    }
    
    // Check for pending changes first
    if (pendingChanges.has(record.id) && 
        pendingChanges.get(record.id)[dataIndex] !== undefined) {
      return pendingChanges.get(record.id)[dataIndex];
    }
    
    // Otherwise return the original value
    return record[dataIndex];
  };
  
  // Save all pending changes
  const saveChanges = () => {
    if (pendingChanges.size === 0) return;
    
    // Convert pendingChanges Map to an array of objects with id and changes
    const changes = [];
    pendingChanges.forEach((fields, id) => {
      changes.push({ id, fields });
    });
    
    // Call the onSave callback with the changes
    if (onSave) {
      onSave(changes);
    }
    
    // Reset pending changes
    setPendingChanges(new Map());
  };
  
  // Set up the components with EditableRow and EditableCell
  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell,
    },
  };
  
  // Transform columns to add props for EditableCell
  const transformedColumns = columns.map(col => {
    if (!col.editable) {
      return col;
    }
    
    return {
      ...col,
      onCell: (record, rowIndex) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        rowIndex,
        colIndex: columns.findIndex(c => c.dataIndex === col.dataIndex && c.editable),
        navigateToCell,
      }),
    };
  });
  
  // Set up the keyboard navigation context
  const keyboardNavContextValue = {
    activeCell,
    setActiveCell,
    navigateToCell,
    activeRowKey,
    setActiveRowKey,
    pendingChanges,
    setPendingChanges,
    getCellValue
  };
  
  return (
    <KeyboardNavContext.Provider value={keyboardNavContextValue}>
      <div>
        <Table
          components={components}
          rowClassName={() => 'editable-row'}
          bordered
          dataSource={data}
          columns={transformedColumns}
          rowKey={rowKey}
          pagination={false}
          size="small"
          scroll={{ x: 'max-content' }}
        />
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <button 
            onClick={saveChanges}
            disabled={pendingChanges.size === 0}
            style={{
              padding: '8px 16px',
              backgroundColor: pendingChanges.size === 0 ? '#d9d9d9' : '#1890ff',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: pendingChanges.size === 0 ? 'not-allowed' : 'pointer'
            }}
          >
            Save Changes ({pendingChanges.size})
          </button>
        </div>
      </div>
    </KeyboardNavContext.Provider>
  );
};

export { EditableTable, EditableRow, EditableCell, EditableContext, KeyboardNavContext };